/**
 * Warning for a campaign spread across inboxes that nothing is warming.
 *
 * Rotation splits a campaign across the chosen inboxes in proportion to each
 * one's daily capacity (see rotation_email.md). A mailbox with no warmup runs
 * at its full published cap from the first send, and takes a correspondingly
 * large share of the campaign; a mailbox that has never built a sending
 * reputation is exactly the one that should not be doing that. So the moment a
 * campaign is spread across more than one inbox, an unwarmed inbox in the set
 * is worth saying out loud.
 *
 * Advisory only. Warmup is not required to send, and a mailbox that has been
 * sending for years needs none, so this never blocks a launch.
 */
import type { SenderEmail } from '@/types/senderEmail'

/** Warmup states that mean a provider is actually working on the mailbox.
 *  'error' is deliberately absent: a connection that is failing is not warming
 *  anything, which is the situation the user most needs to know about. */
const WARMING_STATES = new Set(['warming', 'paused', 'completed', 'inactive'])

export function isWarmed(sender: Pick<SenderEmail, 'warmup_status'>): boolean {
  const status = sender.warmup_status
  return typeof status === 'string' && WARMING_STATES.has(status)
}

/** The selected inboxes with no warmup behind them, in selection order. */
export function unwarmedSelected(selectedIds: number[], all: SenderEmail[]): SenderEmail[] {
  return selectedIds
    .map(id => all.find(s => s.id === id))
    .filter((s): s is SenderEmail => Boolean(s) && !isWarmed(s as SenderEmail))
}

/**
 * The sentence to show, or null when there is nothing to warn about.
 *
 * The rule is about the SELECTION, not its size: if any selected inbox has no
 * warmup behind it, say so. An earlier version stayed silent below two
 * inboxes, on the reasoning that one inbox is not a spreading decision. That
 * was wrong and produced a warning that vanished when it was most deserved:
 * pick a warmed inbox, add an unwarmed one (warning appears), then deselect
 * the warmed one and the remaining selection is entirely unwarmed, yet the
 * warning went away.
 */
export function warmupWarning(selectedIds: number[], all: SenderEmail[]): string | null {
  const unwarmed = unwarmedSelected(selectedIds, all)
  if (unwarmed.length === 0) return null

  const names = unwarmed.map(s => s.email_address).join(', ')
  const subject =
    unwarmed.length === 1
      ? `${names} is not warmed up`
      : unwarmed.length === selectedIds.length
        ? `None of the ${selectedIds.length} inboxes you picked is warmed up`
        : `${unwarmed.length} of the inboxes you picked are not warmed up: ${names}`

  // The proportional-spread reason only exists once there is more than one
  // inbox to spread across; with a single one the risk is the same but the
  // explanation for it is not.
  const why =
    selectedIds.length > 1
      ? `Contacts are spread across your inboxes in proportion to each one's daily limit, so an ` +
        `unwarmed mailbox starts sending at its full cap straight away, which is how a new ` +
        `mailbox lands in spam.`
      : `An unwarmed mailbox starts sending at its full daily cap straight away, which is how a ` +
        `new mailbox lands in spam.`

  const closing =
    unwarmed.length === 1
      ? `or launch anyway if it already sends regularly.`
      : `or launch anyway if these mailboxes already send regularly.`

  return `${subject}. ${why} Connect warmup under Settings > Email Accounts, ${closing}`
}
